import { EventEmitter, Injectable, Output } from '@angular/core';
import {
  Animation,
  Axis,
  Color3,
  Mesh,
  MeshBuilder,
  Quaternion,
  StandardMaterial,
  Tags,
  Vector3,
} from '@babylonjs/core';
import { debounceTime, filter, firstValueFrom } from 'rxjs';
import { IColor, IEntitySettings, IPosition } from 'src/common';
import { minimalSettings } from '../../../assets/settings/settings';
import { AnnotationService } from '../annotation/annotation.service';
import { BabylonService } from '../babylon/babylon.service';
import { LightService } from '../light/light.service';
import { ProcessingService } from '../processing/processing.service';
import {
  createBoundingBox,
  createGround,
  createlocalAxes,
  createWorldAxis,
} from './visualUIHelper';

const isDegreeSpectrum = (value: number) => {
  return value >= 0 && value <= 360 ? value : value > 360 ? 360 : 0;
};

// Some models have uniform scaling, others non-uniform scaling
const getScaleVector = (scale: number | IPosition) => {
  if (typeof scale === 'number') {
    return new Vector3(scale, scale, scale);
  } else {
    return new Vector3(scale.x, scale.y, scale.z);
  }
};

@Injectable({
  providedIn: 'root',
})
export class EntitySettingsService {
  private min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
  private max = new Vector3(Number.MAX_VALUE * -1, Number.MAX_VALUE * -1, Number.MAX_VALUE * -1);
  public initialSize = Vector3.Zero();
  private initialCenterPoint = Vector3.Zero();
  private currentCenterPoint = Vector3.Zero();
  private center: Mesh | undefined;
  public boundingBox: Mesh | undefined;

  @Output() meshSettingsCompleted = new EventEmitter<boolean>();
  public ground: Mesh | undefined;
  private groundInitialSize = 0;
  public localAxisInitialSize = 0;
  public worldAxisInitialSize = 0;

  private entitySettings: IEntitySettings = minimalSettings;

  constructor(
    private babylon: BabylonService,
    private processing: ProcessingService,
    private lights: LightService,
    private annotationService: AnnotationService,
  ) {
    this.processing.state$
      .pipe(
        filter(({ entity, meshes }) => !!entity && !!meshes),
        debounceTime(100),
      )
      .subscribe(({ settings, entity, meshes }) => {
        console.log('EntitySettingsService', entity?._id, { settings, entity, meshes });
        this.entitySettings = settings;
        requestAnimationFrame(() =>
          this.setUpSettings()
            .then(() => console.log('Settings loaded'))
            .catch((err: Error) => console.log('Settings not loaded', err.message)),
        );
      });
  }

  get meshes$() {
    return this.processing.meshes$;
  }

  private async resetInitialValues() {
    this.min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    this.max = new Vector3(Number.MAX_VALUE * -1, Number.MAX_VALUE * -1, Number.MAX_VALUE * -1);
    this.initialSize = Vector3.Zero();
    this.initialCenterPoint = Vector3.Zero();
    this.currentCenterPoint = Vector3.Zero();
    this.groundInitialSize = 0;
    this.localAxisInitialSize = 0;
    this.worldAxisInitialSize = 0;
    this.processing.rotationQuaternion = Quaternion.RotationYawPitchRoll(0, 0, 0);
    this.processing.entityHeight = (0).toFixed(2);
    this.processing.entityWidth = (0).toFixed(2);
    this.processing.entityDepth = (0).toFixed(2);
  }

  private async setUpSettings() {
    const meshes = await firstValueFrom(this.processing.meshes$);
    const isInUpload = await firstValueFrom(this.processing.isInUpload$);
    const hasMeshSettings = await firstValueFrom(this.processing.hasMeshSettings$);
    console.log('Setup settings');
    if (!this.entitySettings) {
      throw new Error('No settings available.');
    }
    if (!meshes || meshes.length === 0) {
      throw new Error('No meshes available.');
    }
    await this.resetInitialValues();
    await this.initialiseSizeValues();
    await this.setUpMeshSettingsHelper();
    await this.createVisualUIMeshSettingsHelper();
    await this.loadSettings();
    if (!isInUpload || !hasMeshSettings) {
      await this.destroyMesh('boundingBox');
      await this.decomposeMeshSettingsHelper();
    }

    // Force redraw annotation markers to fix annotations being offset from the entity if the entity is parented to center
    await this.annotationService.redrawMarker();
  }

  private async initialiseSizeValues() {
    await this.calculateMinMax()
      .then(() => {
        this.initialSize = this.max.subtract(this.min);
        this.processing.entityHeight = this.initialSize.y.toFixed(2);
        this.processing.entityWidth = this.initialSize.x.toFixed(2);
        this.processing.entityDepth = this.initialSize.z.toFixed(2);
        this.initialCenterPoint = this.currentCenterPoint = new Vector3(
          this.max.x - this.initialSize.x / 2,
          this.max.y - this.initialSize.y / 2,
          this.max.z - this.initialSize.z / 2,
        );
      })
      .catch((err: Error) => console.log('Failed intitializing size', err.message));
  }

  private async calculateMinMax() {
    const meshes = await firstValueFrom(this.processing.meshes$);
    if (!meshes) {
      throw new Error('Center missing');
    }
    meshes.forEach(mesh => {
      mesh.computeWorldMatrix(true);
      // see if mesh is visible or just a dummy
      const bi = mesh.getBoundingInfo();
      if (bi.diagonalLength !== 0) {
        // compare min max values
        const minimum = bi.boundingBox.minimumWorld;
        const maximum = bi.boundingBox.maximumWorld;

        if (minimum.x < this.min.x) {
          this.min.x = minimum.x;
        }
        if (minimum.y < this.min.y) {
          this.min.y = minimum.y;
        }
        if (minimum.z < this.min.z) {
          this.min.z = minimum.z;
        }
        if (maximum.x > this.max.x) {
          this.max.x = maximum.x;
        }
        if (maximum.y > this.max.y) {
          this.max.y = maximum.y;
        }
        if (maximum.z > this.max.z) {
          this.max.z = maximum.z;
        }
      }
    });
  }

  private async setUpMeshSettingsHelper() {
    const meshes = await firstValueFrom(this.processing.meshes$);
    if (!meshes) {
      throw new Error('No meshes available.');
    }
    this.center?.dispose();

    this.center = MeshBuilder.CreateBox('center', { size: 0.01 }, this.babylon.getScene());
    this.center.isPickable = false;
    Tags.AddTagsTo(this.center, 'center');
    this.center.isVisible = false;

    // rotation to zero
    this.center.rotationQuaternion = this.processing.rotationQuaternion;

    // case iiif position set -> set to position
    // else case position model to origin of the world coordinate system
    if (this.entitySettings.position !== undefined) {
      let temp_vec = new Vector3();
      if (this.entitySettings.position) {
        temp_vec.x = this.entitySettings.position.x;
        temp_vec.y = this.entitySettings.position.y;
        temp_vec.z = this.entitySettings.position.z;

        console.log('Temp Vec', temp_vec, this.entitySettings.position);
      }
      console.log('Center Position Settings', this.entitySettings.position.x, this.entitySettings.position.y, this.entitySettings.position.z);
      this.center.position = new Vector3(this.entitySettings.position.x, this.entitySettings.position.y, this.entitySettings.position.z);
      console.log('Center Position', this.center.position);
    } else {
      if (this.min.x > 0) {
        this.center.position.x = -this.min.x;
      }
      if (this.min.x < 0) {
        this.center.position.x = Math.abs(this.min.x);
      }
      if (this.min.y > 0) {
        this.center.position.y = -this.min.y;
      }
      if (this.min.y < 0) {
        this.center.position.y = Math.abs(this.min.y);
      }
      if (this.min.z > 0) {
        this.center.position.z = -this.min.z;
      }
      if (this.min.z < 0) {
        this.center.position.z = Math.abs(this.min.z);
      }
    }
    this.currentCenterPoint = new Vector3(
      this.initialSize.x / 2,
      this.initialSize.y / 2,
      this.initialSize.z / 2,
    );

    // pivot to the center of the (visible) model
    this.center.setPivotPoint(this.initialCenterPoint);

    meshes.forEach(mesh => {
      if (!mesh.parent) {
        mesh.parent = this.center as Mesh;
        Tags.AddTagsTo(mesh, 'parentedMesh');
      }
    });
  }

  private async loadSettings() {
    const mediaType = await firstValueFrom(this.processing.mediaType$);
    const hasMeshSettings = await firstValueFrom(this.processing.hasMeshSettings$);
    await this.initialiseCamera();
    if (!(this.entitySettings as any).skipInitialCameraSetup) {
      await this.loadCameraInititalPosition();
    }
    this.loadBackgroundEffect();
    this.loadBackgroundColor();
    this.initialiseLights();
    if (hasMeshSettings || mediaType === 'audio') {
      await this.loadRotation();
      await this.loadScaling();
      await this.loadTranslation();
    }
  }

  public restoreSettings() {
    this.loadCameraInititalPosition();
    this.loadBackgroundEffect();
    this.loadBackgroundColor();
    this.loadPointLightPosition();
    this.loadLightIntensityAllLights();
  }

  private async initialiseCamera() {
    if (!this.entitySettings) {
      console.error(this);
      throw new Error('Settings missing');
    }
    const mediaType = await firstValueFrom(this.processing.mediaType$);
    const isInUpload = await firstValueFrom(this.processing.isInUpload$);
    const isDefault = await firstValueFrom(this.processing.defaultEntityLoaded$);
    const scale = getScaleVector(this.entitySettings.scale);
    const isModel = mediaType === 'model' || mediaType === 'entity';
    const isSplat = mediaType === 'splat';

    if (isSplat) {
      const meshes = await firstValueFrom(this.processing.meshes$);
      console.log(meshes.at(0)?.getBoundingInfo().diagonalLength);
      const target = meshes.at(0)?.getBoundingInfo()?.boundingSphere.minimum.negate();
      if (target) this.currentCenterPoint = target;
    }

    let diagonalLength = 0;
    if (this.boundingBox) {
      const bi = this.boundingBox.getBoundingInfo();
      diagonalLength = bi.diagonalLength;
    } else {
      diagonalLength = Math.sqrt(
        this.initialSize.x * scale.x * (this.initialSize.x * scale.x) +
          this.initialSize.y * scale.y * (this.initialSize.y * scale.y) +
          this.initialSize.z * scale.z * (this.initialSize.z * scale.z),
      );
    }
    const max = !isDefault ? (isInUpload && isModel ? diagonalLength * 2.5 : diagonalLength) : 87.5;
    await this.babylon.cameraManager.setUpActiveCamera(max, mediaType!);

    if (isInUpload && mediaType !== 'audio') {
      const position = new Vector3(
        isModel ? Math.PI / 4 : -Math.PI / 2,
        isModel ? Math.PI / 4 : Math.PI / 2,
        isSplat ? diagonalLength * 0.01 : diagonalLength * 1.25,
      );
      const target = this.currentCenterPoint;
      console.log('target', target);
      console.log('position', position);
      this.entitySettings.cameraPositionInitial = {
        position,
        target,
      };
    }
  }

  public async decomposeMeshSettingsHelper() {
    const center = this.center;
    if (!center) {
      throw new Error('Center missing');
    }
    const meshes = this.babylon.getScene().getMeshesByTags('parentedMesh');
    if (!meshes) {
      throw new Error('Meshes missing');
    }
    await meshes.forEach(mesh => {
      mesh.computeWorldMatrix();
      const abs = mesh.absolutePosition;
      if (!mesh.rotationQuaternion) {
        mesh.rotationQuaternion = Quaternion.RotationYawPitchRoll(
          mesh.rotation.y,
          mesh.rotation.x,
          mesh.rotation.z,
        );
      }
      mesh.parent = null;
      mesh.position = abs;
      const meshRotation = mesh.rotationQuaternion;
      mesh.rotationQuaternion = this.processing.rotationQuaternion.multiply(meshRotation);
      center.computeWorldMatrix();
      mesh.scaling.x *= center.scaling.x;
      mesh.scaling.y *= center.scaling.y;
      mesh.scaling.z *= center.scaling.z;
    });
    await this.destroyMesh('center');
  }

  public async destroyVisualUIMeshSettingsHelper() {
    await this.destroyMesh('boundingBox');
    await this.destroyMesh('worldAxis');
    await this.destroyMesh('localAxis');
    await this.destroyMesh('ground');
    await this.initialiseCamera();
  }

  private destroyMesh(tag: string) {
    this.babylon
      .getScene()
      .getMeshesByTags(tag)
      .map(mesh => mesh.dispose());
  }

  /*
   * Mesh Settings
   */

  // Rotation
  public async loadRotation() {
    if (!this.center) {
      throw new Error('Center missing');
    }
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    if (!this.center.rotationQuaternion) {
      throw new Error('RotationQuaternion for center missing');
    }

    this.entitySettings.rotation.x = isDegreeSpectrum(this.entitySettings.rotation.x);
    this.entitySettings.rotation.y = isDegreeSpectrum(this.entitySettings.rotation.y);
    this.entitySettings.rotation.z = isDegreeSpectrum(this.entitySettings.rotation.z);

    const start = this.processing.rotationQuaternion;
    const rotationQuaternion = Quaternion.RotationYawPitchRoll(0, 0, 0);
    const rotationQuaternionX = Quaternion.RotationAxis(
      Axis['X'],
      (Math.PI / 180) * this.entitySettings.rotation.x,
    );
    let end = rotationQuaternionX.multiply(rotationQuaternion);
    const rotationQuaternionY = Quaternion.RotationAxis(
      Axis['Y'],
      (Math.PI / 180) * this.entitySettings.rotation.y,
    );
    end = rotationQuaternionY.multiply(end);
    const rotationQuaternionZ = Quaternion.RotationAxis(
      Axis['Z'],
      (Math.PI / 180) * this.entitySettings.rotation.z,
    );
    end = rotationQuaternionZ.multiply(end);
    this.animatedMovement(start, end);
    this.processing.rotationQuaternion = end;
    this.center.rotationQuaternion = end;
  }

  private async animatedMovement(start: Quaternion, end: Quaternion) {
    if (!this.center) {
      throw new Error('Center missing');
    }
    const anim = new Animation(
      'anim',
      'rotationQuaternion',
      120,
      Animation.ANIMATIONTYPE_QUATERNION,
      Animation.ANIMATIONLOOPMODE_RELATIVE,
    );
    const frame = [
      { frame: 0, value: start },
      { frame: 100, value: end },
    ];
    anim.setKeys(frame);
    this.center.animations = [];
    this.center.animations.push(anim);
    await this.babylon
      .getScene()
      .beginAnimation(this.center, 0, 100, false, undefined, undefined, undefined, false);
  }

  // Size
  public loadScaling() {
    if (!this.center) {
      throw new Error('Center missing');
    }
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const scale = getScaleVector(this.entitySettings.scale);
    this.center.scaling = new Vector3(scale.x, scale.y, scale.z);

    this.processing.entityHeight = (this.initialSize.y * scale.y).toFixed(2);
    this.processing.entityWidth = (this.initialSize.x * scale.x).toFixed(2);
    this.processing.entityDepth = (this.initialSize.z * scale.z).toFixed(2);
  }

  public loadTranslation() {
    if (this.center) {
      this.center.position = this.center.position.add(
        new Vector3(
          this.entitySettings.translate?.x,
          this.entitySettings.translate?.y,
          this.entitySettings.translate?.z,
        ),
      );
      console.log('Center Position 20', this.center.position);
    }
  }

  public async createVisualUIMeshSettingsHelper() {
    if (!this.center) {
      throw new Error('Center missing');
    }
    const hasMeshSettings = await firstValueFrom(this.processing.hasMeshSettings$);
    const isInUpload = await firstValueFrom(this.processing.isInUpload$);
    const scene = this.babylon.getScene();
    const size = Math.max(
      +this.processing.entityHeight,
      +this.processing.entityWidth,
      +this.processing.entityDepth,
    );
    this.boundingBox = createBoundingBox(
      scene,
      this.center,
      this.initialSize,
      this.initialCenterPoint,
    );
    this.boundingBox.renderingGroupId = 2;
    if (isInUpload && hasMeshSettings) {
      this.worldAxisInitialSize = size * 1.2;
      this.localAxisInitialSize = size * 1.1;
      this.groundInitialSize = size * 1.2;
      createWorldAxis(scene, this.worldAxisInitialSize);
      createlocalAxes(scene, this.localAxisInitialSize, this.center, this.initialCenterPoint);
      this.ground = createGround(scene, this.groundInitialSize);
      this.ground.position = this.center.position.clone();
      this.setGroundMaterial();
    }
  }

  // Set the color for the helper grid
  public setGroundMaterial(color?: IColor) {
    const scene = this.babylon.getScene();
    const oldMat = scene.getMaterialByName('GroundPlaneMaterial');
    const material = new StandardMaterial('GroundPlaneMaterial', scene);
    material.diffuseColor = new Color3(
      (color ? color.r : 255) / 255,
      (color ? color.g : 255) / 255,
      (color ? color.b : 255) / 255,
    );
    material.wireframe = true;
    if (this.ground) {
      this.ground.material = material;
    }
    if (oldMat) {
      oldMat.dispose();
    }
  }

  // Load cameraPosition
  public async loadCameraInititalPosition() {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const camera = this.entitySettings.cameraPositionInitial;
    const position = new Vector3(camera.position.x, camera.position.y, camera.position.z);
    const target = new Vector3(camera.target.x, camera.target.y, camera.target.z);
    this.babylon.cameraManager.cameraDefaults$.next({ position, target });
    this.babylon.cameraManager.moveActiveCameraToPosition(position);
    this.babylon.cameraManager.setActiveCameraTarget(target);
  }

  // background: color, effect
  loadBackgroundColor() {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const color = this.entitySettings.background.color;
    this.babylon.setBackgroundColor(color);
  }

  loadBackgroundEffect() {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    this.babylon.setBackgroundImage(this.entitySettings.background.effect);
  }

  // lights: up, down, pointlight
  // Ambientlights

  private initialiseLights() {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const pointLight = this.lights.getLightByType('pointLight');
    if (pointLight) {
      const position = new Vector3(
        pointLight.position.x,
        pointLight.position.y,
        pointLight.position.z,
      );
      this.lights.initialisePointLight(pointLight.intensity, position);
    }
    const hemisphericLightUp = this.lights.getLightByType('ambientlightUp');
    if (hemisphericLightUp) {
      this.lights.initialiseAmbientLight('up', hemisphericLightUp.intensity);
    }
    const hemisphericLightDown = this.lights.getLightByType('ambientlightDown');
    if (hemisphericLightDown) {
      this.lights.initialiseAmbientLight('down', hemisphericLightDown.intensity);
    }
  }

  public loadLightIntensityAllLights() {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const ambientlightUp = this.lights.getLightByType('ambientlightUp');
    if (ambientlightUp) {
      this.lights.setLightIntensity('ambientlightUp', ambientlightUp.intensity);
    }
    const ambientlightDown = this.lights.getLightByType('ambientlightDown');
    if (ambientlightDown) {
      this.lights.setLightIntensity('ambientlightDown', ambientlightDown.intensity);
    }
    const pointLight = this.lights.getLightByType('pointLight');
    if (pointLight) {
      this.lights.setLightIntensity('pointLight', pointLight.intensity);
    }
  }

  public loadLightIntensity(lightType: string) {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const light = this.lights.getLightByType(lightType);
    if (light) {
      this.lights.setLightIntensity(lightType, light.intensity);
    }
  }

  public loadPointLightPosition() {
    if (!this.entitySettings) {
      throw new Error('Settings missing');
    }
    const pointLight = this.lights.getLightByType('pointLight');
    if (pointLight) {
      const position = new Vector3(
        pointLight.position.x,
        pointLight.position.y,
        pointLight.position.z,
      );
      this.lights.setPointLightPosition(position);
    }
  }
}
